import { ChevronRightIcon, PhoneRotaryIcon } from '@forwardflow/icons'
import { usePhoneNumbers } from '../hooks/usePhoneNumbers'
import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import cn from 'classnames'
import { CampaignNumberGroupTabs } from './tabs/CampaignNumberGroupTabs'
import { ChangeRealtorNumberView } from './ChangeRealtorNumberView'
import { ConfirmRealtorNumberChangeModal } from './modals/ConfirmRealtorNumberChangeModal'
import { ActiveNumbersView } from './tabs/ActiveNumbersView'
import { InactiveNumbersView } from './tabs/InactiveNumbersView'
import { IPhoneNumber } from '../interface'

const PhoneNumberGroupMenuItem = ({
   label,
   onClick,
   count,
   isSelected = false,
}: {
   label: string
   onClick: () => void
   count: number
   isSelected: boolean
}) => {
   return (
      <motion.button
         onClick={onClick}
         className={`flex items-center justify-between p-6 pr-3 cursor-pointer w-full ${
            isSelected ? 'bg-[#9061DA]/50' : 'hover:bg-[#9061DA]/10'
         }`}
         whileTap={{ scale: 0.95 }}
      >
         <p className="text-white text-sm font-medium">{label}</p>
         <div className="flex items-center gap-x-2">
            <p
               className={cn(
                  'text-white text-sm font-medium',
                  isSelected ? 'text-white' : 'text-white/70'
               )}
            >
               {count}
            </p>
            <ChevronRightIcon
               className={cn(
                  'text-white w-4 h-4',
                  isSelected ? 'opacity-100' : 'opacity-0'
               )}
            />
         </div>
      </motion.button>
   )
}

export const PhoneNumberManager = () => {
   const {
      activePhoneNumbers,
      groupedActivePhoneNumbers,
      groupedInactivePhoneNumbers,
      realtorNumber,
      campaignList,
      useCampaignPhoneNumberGroups,
      updateRealtorNumber,
      isLoadingPhoneNumbers
   } = usePhoneNumbers()

   const [selectedTab, setSelectedTab] = useState<
      'active' | 'inactive' | string
   >('active')
   const [isChangingRealtorNumber, setIsChangingRealtorNumber] = useState(false)
   const [selectedNewNumber, setSelectedNewNumber] =
      useState<IPhoneNumber | null>(null)
   const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
   const [isUpdatingRealtor, setIsUpdatingRealtor] = useState(false)
   const campaignPhoneNumberGroups = useCampaignPhoneNumberGroups(selectedTab)

   const handleSelectRealtorNumber = (number: IPhoneNumber) => {
      setSelectedNewNumber(number)
      setIsConfirmModalOpen(true)
   }

   const handleConfirmChange = async () => {
      if (selectedNewNumber && realtorNumber) {
         setIsUpdatingRealtor(true)
         try {
            await updateRealtorNumber(
               selectedNewNumber.phoneNumber.phoneNumber
            )
            console.log('Successfully updated realtor number')
            setIsConfirmModalOpen(false)
            setIsChangingRealtorNumber(false)
            setSelectedNewNumber(null)
         } catch (error) {
            console.error('Error updating realtor number:', error)
         } finally {
            setIsUpdatingRealtor(false)
         }
      }
   }

   const handleCloseModal = () => {
      if (isUpdatingRealtor) return
      setIsConfirmModalOpen(false)
      setSelectedNewNumber(null)
   }

   useEffect(() => {
      setIsChangingRealtorNumber(false)
   }, [selectedTab])

   if (isLoadingPhoneNumbers) {
      return (
         <div className="rounded-lg bg-[#9061DA]/[0.05] w-full max-w-[1064px] h-[calc(100vh-120px)] flex items-center justify-center">
            <div className="flex flex-col items-center gap-y-4">
               <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                     duration: 1,
                     repeat: Infinity,
                     ease: 'linear',
                  }}
                  className="w-12 h-12 border-4 border-[#9061DA] border-t-transparent rounded-full"
               />
               <p className="text-white/60 text-sm">Loading phone numbers...</p>
            </div>
         </div>
      )
   }

   return (
      <div className="rounded-lg bg-[#9061DA]/[0.05] grid grid-cols-[2fr_5fr] w-full max-w-[1064px] h-[calc(100vh-120px)]">
         <div className="bg-[#9061DA]/[0.05] rounded-lg">
            <div className="flex items-center gap-2 p-6">
               <PhoneRotaryIcon className="text-white w-6 h-6" />
               <p className="text-white text-lg font-medium">Phone Numbers</p>
            </div>
            <div>
               <PhoneNumberGroupMenuItem
                  label="Active Numbers"
                  onClick={() => setSelectedTab('active')}
                  count={groupedActivePhoneNumbers.length}
                  isSelected={selectedTab === 'active'}
               />
               <PhoneNumberGroupMenuItem
                  label="Inactive Numbers"
                  onClick={() => setSelectedTab('inactive')}
                  count={groupedInactivePhoneNumbers.length}
                  isSelected={selectedTab === 'inactive'}
               />
               {campaignList.map((campaign) => (
                  <PhoneNumberGroupMenuItem
                     key={campaign.campaignId}
                     label={campaign.campaignName}
                     onClick={() => setSelectedTab(campaign.campaignId)}
                     count={campaign.numberCount}
                     isSelected={selectedTab === campaign.campaignId}
                  />
               ))}
            </div>
         </div>
         <div className="p-4 pr-0 flex flex-col gap-y-6 h-full overflow-y-auto max-h-[calc(100vh-120px)]">
            {(() => {
               switch (selectedTab) {
                  case 'active':
                     return isChangingRealtorNumber ? (
                        <ChangeRealtorNumberView
                           activePhoneNumbers={
                              activePhoneNumbers as IPhoneNumber[]
                           }
                           currentRealtorNumber={realtorNumber as IPhoneNumber}
                           onSelectNumber={handleSelectRealtorNumber}
                           onCancel={() => setIsChangingRealtorNumber(false)}
                        />
                     ) : (
                        <ActiveNumbersView
                           realtorNumber={realtorNumber as IPhoneNumber}
                           groupedActivePhoneNumbers={groupedActivePhoneNumbers}
                           onChangeRealtorNumber={() =>
                              setIsChangingRealtorNumber(true)
                           }
                        />
                     )
                  case 'inactive':
                     return (
                        <InactiveNumbersView
                           groupedInactivePhoneNumbers={
                              groupedInactivePhoneNumbers
                           }
                        />
                     )
                  default:
                     return (
                        <CampaignNumberGroupTabs
                           numberGroups={campaignPhoneNumberGroups}
                           campaignId={selectedTab}
                        />
                     )
               }
            })()}
         </div>

         {/* Confirmation Modal */}
         {selectedNewNumber && (
            <ConfirmRealtorNumberChangeModal
               isOpen={isConfirmModalOpen}
               onClose={handleCloseModal}
               onConfirm={handleConfirmChange}
               oldNumber={realtorNumber as IPhoneNumber}
               newNumber={selectedNewNumber}
               isLoading={isUpdatingRealtor}
            />
         )}
      </div>
   )
}
